import { JackpotCounter } from "../Jackpot/JackpotCounter";
import { JackpotHistoryDialog } from "../Jackpot/JackpotHistoryDialog";
import { Scaling } from "../../../Utils/Scaling";
import { CreateFrameTimer, FrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../../../Utils/FrameTimer";
import { HtmlController } from "../../../HTMLComponents/HtmlController";
import { JackpotGame } from "../Jackpot/JackpotGame";
import { AppContext } from "../../../../Common/AppContext";
import { Communication } from "neo-games/Engine/index";
import { GameManager } from "../../../Managers/GameManager";
import { ModuleManager } from "../../../Managers/ModuleManager";
import { GameData } from "../Datas/GameData";
import { DataManager } from "../../../Managers/DataManager";

type IProgressiveBonusLevel = Communication.Game.IProgressiveBonusLevel;
type ProgressiveBonusGetInfoResponse = Communication.Game.ProgressiveBonusGetInfoResponse;

export class Jackpot {
    //******************************************************************************************************************
    //  VARIABLES
    //******************************************************************************************************************

    public static readonly JackpotTheme: string = "SPJackpot";

    public static Height: number = 0;

    private static BonusId: number = -1;
    private static CountersCount: number = 4;

    private static WidgetRoot: HTMLElement;
    private static GameRoot: HTMLElement;
    private static Counters: Array<JackpotCounter> = [];
    private static HistoryDialogs: Array<JackpotHistoryDialog> = [];
    private static Game: JackpotGame;

    private static RefreshInterval: number = 15; //in seconds
    private static RefreshTimerId: number;

    private static TouchEvent: any;
    private static Offline: boolean = false;

    private static Timer: FrameTimerEventEmitter;

    //******************************************************************************************************************
    //  INITIALIZE & SHOW
    //******************************************************************************************************************

    public static initialize(): void {
        if (AppContext && AppContext.gameConfiguration.ProgressiveBonusId > -1) {
            document.body.classList.add("jpt-on");
            Jackpot.WidgetRoot = document.getElementById("jackpotWidget");
            Jackpot.GameRoot = document.getElementById("jackpotGame");
            Jackpot.BonusId = AppContext.gameConfiguration.ProgressiveBonusId;

            Jackpot.createCounters();
            Jackpot.createHistoryDialogs();
            // Jackpot.createBonusGame();

            Jackpot.bindElements();

            Jackpot.getBonusInfo(Jackpot.initJackpotDataReceived);

            if (GameManager.gameClient) {
                GameManager.gameClient.setBonusId(Jackpot.BonusId);
            }
        } else {
            Jackpot.clearJackpot();
        }
    }

    public static reinitialize(): void {
        if (Jackpot.Offline) {
            Jackpot.Offline = false;
            Jackpot.refreshBonusInfo();
            Jackpot.refreshCounters();
        }
    }

    public static show(): void {
        if (Jackpot.WidgetRoot) {
            if (Jackpot.Counters.length > 0) {
                for (let i = 0; i < Jackpot.CountersCount; i++) {
                    Jackpot.Counters[i].createCurrency(DataManager.getDataById(GameData.DataCurrency));
                    Jackpot.HistoryDialogs[i].updateDataWithCurrency();
                }

                Jackpot.Height = Jackpot.Counters[0].getHeight();
                Scaling.PortraitMobileRatio = 0.76; //ToDo hot fix 222732
                Scaling.resizeRoot();
            }
        }
    }

    private static clearJackpot(): void {
        let jackpotWidget = document.getElementById("jackpotWidget");
        let jackpotGame = document.getElementById("jackpotGame");
        if (jackpotWidget && jackpotWidget.parentNode && jackpotGame && jackpotGame.parentNode) {
            jackpotWidget.parentNode.removeChild(jackpotWidget);
            jackpotGame.parentNode.removeChild(jackpotGame);
        }
    }

    //******************************************************************************************************************
    //  CREATE
    //******************************************************************************************************************

    private static createCounters(): void {
        if (Jackpot.WidgetRoot) {
            Jackpot.getCustomContent("jackpotLogo.jpg", Jackpot.createLogo, Jackpot.createLogo);

            Jackpot.createElement("div", "jackpotCountersBg", Jackpot.WidgetRoot);
            let holder = Jackpot.createElement("div", "jackpotCounters", Jackpot.WidgetRoot);
            holder.id = "jackpotCounters";

            for (let i = 0; i < Jackpot.CountersCount; i++) {
                let counter = new JackpotCounter(i + 1, holder, Jackpot.refreshCounters);

                Jackpot.Counters.push(counter);
            }
        }
    }

    private static createLogo(contentUrl: string = ""): void {
        let logo = Jackpot.createElement("div", "jackpotLogo", Jackpot.WidgetRoot);
        logo = Jackpot.createElement("div", "", logo);
        logo.id = "jackpotLogo";

        if (contentUrl != "") {
            logo.style.background = `url(${contentUrl})`;
        }
    }

    private static createHistoryDialogs(): void {
        if (Jackpot.WidgetRoot) {
            let holder = Jackpot.createElement("div", "jackpotHistory", Jackpot.WidgetRoot);

            for (let i = 0; i < Jackpot.CountersCount; i++) {
                let dialog = new JackpotHistoryDialog(i + 1, holder);

                Jackpot.HistoryDialogs.push(dialog);
            }
        }
    }

    private static createBonusGame(): void {
        if (Jackpot.GameRoot) {
            let holder = Jackpot.createElement("div", "jackpotGameHolder", Jackpot.GameRoot);
            Jackpot.Game = new JackpotGame(holder);
        }
    }

    //******************************************************************************************************************
    //  REFRESH
    //******************************************************************************************************************

    public static refreshCounters(): void {
        for (let i = 0; i < Jackpot.CountersCount; i++) {
            Jackpot.Counters[i].refreshWithReset();
        }
    }

    public static showError(useResetAnim: boolean = false): void {
        clearTimeout(this.RefreshTimerId);

        if (Jackpot.WidgetRoot) {
            if (Jackpot.Counters && Jackpot.HistoryDialogs) {
                Jackpot.Offline = true;

                for (let i = 0; i < Jackpot.CountersCount; i++) {
                    Jackpot.Counters[i].error(useResetAnim);
                    Jackpot.HistoryDialogs[i].error(useResetAnim);
                }
            }
        }
    }

    //******************************************************************************************************************
    //  REFRESH
    //******************************************************************************************************************

    public static refreshBonusInfo(): void {
        clearTimeout(this.RefreshTimerId);

        Jackpot.getBonusInfo(Jackpot.jackpotDataReceive);
    }

    public static refreshBonusInfoInMiniGame(overrideLevelData: IProgressiveBonusLevel): void {
        clearTimeout(this.RefreshTimerId);

        Jackpot.getBonusInfo(Jackpot.jackpotDataReceive, { overrideLeveldata: overrideLevelData, priority: true });
    }

    public static refreshBonusInfoWithWinValue(overrideLevelData: IProgressiveBonusLevel): void {
        clearTimeout(this.RefreshTimerId);

        Jackpot.getBonusInfo(Jackpot.jackpotDataReceive, {
            overrideLeveldata: overrideLevelData,
            priority: true,
            fromMiniGame: overrideLevelData.code,
        });
    }

    public static refreshBonusInfoBeforeSpin(spinCallback: () => void): void {
        clearTimeout(this.RefreshTimerId);

        Jackpot.getBonusInfoBeforeSpin(spinCallback, { priority: true });
    }

    //******************************************************************************************************************
    //  REQUESTS
    //******************************************************************************************************************

    private static getBonusInfo(
        callback: (response: ProgressiveBonusGetInfoResponse, customData?: any) => void,
        customData?: any,
    ): void {
        if (GameManager.gameClient) {
            GameManager.gameClient
                .sendProgressiveBonusGetInfoRequest(Jackpot.BonusId)
                .then((response: ProgressiveBonusGetInfoResponse) => {
                    callback(response, customData);
                    return null;
                })
                .catch((error) => {
                    Jackpot.showError(true);
                });
        }
    }

    private static getBonusInfoBeforeSpin(callback: () => void, customData?: any): void {
        if (GameManager.gameClient) {
            GameManager.gameClient
                .sendProgressiveBonusGetInfoRequest(Jackpot.BonusId)
                .then((response: ProgressiveBonusGetInfoResponse) => {
                    Jackpot.jackpotDataReceive(response, customData);
                    callback();
                    return null;
                })
                .catch((error) => {
                    Jackpot.showError(true);
                    callback();
                });
        }
    }

    //******************************************************************************************************************
    //  HANDLE RESPONSE
    //******************************************************************************************************************

    private static initJackpotDataReceived(response: ProgressiveBonusGetInfoResponse, customData?: any): void {
        if (response != null) {
            Jackpot.RefreshInterval = response.refreshInterval;

            for (let i = 0; i < Jackpot.CountersCount; i++) {
                Jackpot.Counters[i].initialize(response.levels[i], response.timestamp);
                Jackpot.HistoryDialogs[i].initialize(response.levels[i]);
            }
        } else {
            for (let i = 0; i < Jackpot.CountersCount; i++) {
                Jackpot.Counters[i].error();
                Jackpot.HistoryDialogs[i].error();
            }
        }

        Jackpot.RefreshTimerId = setTimeout(Jackpot.refreshBonusInfo, Jackpot.RefreshInterval * 1000);
    }

    private static jackpotDataReceive(response: ProgressiveBonusGetInfoResponse, customData?: any): void {
        if (response != null) {
            Jackpot.RefreshInterval = response.refreshInterval;

            let winOverride = customData != null && customData["overrideLeveldata"] != null;
            let priority = customData != null && customData["priority"] != null;
            let fromMiniGame =
                customData != null && customData["fromMiniGame"] != null ? customData["fromMiniGame"] : "";

            if (winOverride) {
                response = Jackpot.updateReponseData(response, customData["overrideLeveldata"]);
            }

            for (let i = 0; i < Jackpot.CountersCount; i++) {
                Jackpot.Counters[i].assign(
                    response.levels[i],
                    response.timestamp,
                    priority,
                    response.levels[i]["overridedLastWinAmount"],
                    fromMiniGame == response.levels[i].code,
                );
                Jackpot.HistoryDialogs[i].assign(response.levels[i]);
            }
        } else {
            for (let i = 0; i < Jackpot.CountersCount; i++) {
                Jackpot.Counters[i].error();
                Jackpot.HistoryDialogs[i].error();
            }
        }

        Jackpot.RefreshTimerId = setTimeout(Jackpot.refreshBonusInfo, Jackpot.RefreshInterval * 1000);
    }

    private static updateReponseData(
        response: ProgressiveBonusGetInfoResponse,
        overrideLevelData: IProgressiveBonusLevel,
    ): ProgressiveBonusGetInfoResponse {
        if (overrideLevelData != null) {
            for (let i = 0; i < Jackpot.CountersCount; i++) {
                if (response.levels[i].code == overrideLevelData.code) {
                    //to always show minigame win value in counter
                    if (overrideLevelData.lastWinAmount != null) {
                        response.levels[i]["overridedLastWinAmount"] = overrideLevelData.lastWinAmount;
                    } else {
                        response.levels[i] = overrideLevelData;
                    }
                }
            }
        }

        return response;
    }

    //******************************************************************************************************************
    //  BIND EVENTS
    //******************************************************************************************************************

    private static bindElements(): void {
        for (let i = 0; i < Jackpot.Counters.length; i++) {
            if (window["CHANNEL"] === "mobile") {
                Jackpot.Counters[i]
                    .getRoot()
                    .addEventListener("click", (event: Event) => Jackpot.toggleHistoryDialog(event, i));

                //prevent close when clicked into dialog
                for (let i = 0; i < Jackpot.HistoryDialogs.length; i++) {
                    Jackpot.HistoryDialogs[i]
                        .getRoot()
                        .addEventListener("click", (event: Event) => Jackpot.preventDefault(event));
                    Jackpot.HistoryDialogs[i]
                        .getRoot()
                        .addEventListener("touchstart", (event: Event) => Jackpot.preventDefault(event));
                }
            } else {
                Jackpot.Counters[i].getRoot().addEventListener("click", Jackpot.showHistoryDialog.bind(this, i));
                Jackpot.Counters[i].getRoot().addEventListener("mouseover", Jackpot.showHistoryDialog.bind(this, i));
                Jackpot.Counters[i].getRoot().addEventListener("mouseout", Jackpot.hideHistoryDialog.bind(this, i));
            }
        }
    }

    //******************************************************************************************************************
    //  HANDLE HISTORY DIALOGS
    //******************************************************************************************************************

    public static closeAllHistoryDialogs(): void {
        if (Jackpot.WidgetRoot) {
            Jackpot.WidgetRoot.removeEventListener("click", Jackpot.closeAllHistoryDialogs);
            for (let i = 0; i < Jackpot.HistoryDialogs.length; i++) {
                Jackpot.HistoryDialogs[i].closeDialog();
            }
        }
    }

    private static showHistoryDialog(index: number): void {
        this.closeAllHistoryDialogs();

        if (Jackpot.HistoryDialogs[index]) {
            if (window["CHANNEL"] === "mobile") {
                HtmlController.dispatchAction("");
                Jackpot.WidgetRoot.addEventListener("click", Jackpot.closeAllHistoryDialogs);
            }

            Jackpot.TouchEvent = Jackpot.hideHistoryDialog.bind(this, index);
            document.addEventListener("touchstart", Jackpot.TouchEvent);

            Jackpot.HistoryDialogs[index].openDialog();
        }
    }

    private static toggleHistoryDialog(event: Event, index: number): void {
        event.preventDefault();
        event.stopPropagation();

        if (Jackpot.HistoryDialogs[index] && !Jackpot.HistoryDialogs[index].isOpen()) {
            this.showHistoryDialog(index);
        } else {
            this.closeAllHistoryDialogs();
        }
    }

    private static hideHistoryDialog(index: number): void {
        if (Jackpot.HistoryDialogs[index]) {
            Jackpot.HistoryDialogs[index].closeDialog();
            document.removeEventListener("touchstart", Jackpot.TouchEvent);
        }
    }

    private static preventDefault(event: Event): void {
        event.preventDefault();
        event.stopPropagation();
    }

    //******************************************************************************************************************
    //  MINI GAME
    //******************************************************************************************************************

    /**
     * Send minigame init request
     * @param autostart - start minigame after init request past
     * @param callback - if init request is ok and autoplay mode is true, run callback (disable base ui)
     */
    public static initMiniGame(autostart: boolean, callback: () => void): void {
        if (!Jackpot.Game) {
            Jackpot.createBonusGame();
        }

        Jackpot.Game.init(autostart, callback);
    }

    public static startMiniGame(): void {
        if (!Jackpot.Game.hasStartedGame()) {
            Jackpot.Game.start();
        }
    }

    public static hasActiveGame(): boolean {
        if (!Jackpot.Game) {
            return false;
        }

        return Jackpot.Game.hasStartedGame();
    }

    public static hasInitializedGame(): boolean {
        return Jackpot.Game && Jackpot.Game.hasInitializedGame();
    }

    /**
     * Return if the mini game is prepared and ready to play.
     */
    public static hasMiniGame(): boolean {
        //check already running game
        if (Jackpot.Game && Jackpot.Game.hasStartedGame()) {
            return true;
        }

        //check counters
        if (Jackpot.countersInError()) {
            return false;
        }

        if (Jackpot.Game && Jackpot.Game.hasInitializedGame()) {
            return true;
        }

        return GameManager.gameResponse["progressiveBonus"] && GameManager.gameResponse["progressiveBonus"].ticket;
    }

    public static disableMiniGameAutostart(): void {
        if (Jackpot.Game) {
            Jackpot.Game.disableAutostart();
        }
    }

    public static waitForMiniGameInitialization(): boolean {
        return (
            AppContext &&
            (AppContext.gameConfiguration.ProgressiveBonusTicketConstraint == "WithinGameRound" ||
                AppContext.gameConfiguration.ProgressiveBonusTicketConstraint == "AfterGameRound")
        );
    }

    //******************************************************************************************************************
    //  HELPER
    //******************************************************************************************************************

    private static createElement(tag: string, className: string, rootElem?: HTMLElement): HTMLElement {
        let elem = document.createElement(tag);
        elem.setAttribute("class", className);

        if (rootElem) {
            rootElem.appendChild(elem);
        }

        return elem;
    }

    private static countersInError(): boolean {
        if (Jackpot.Counters && Jackpot.Counters.length > 0) {
            for (let i = 0; i < Jackpot.CountersCount; i++) {
                if (Jackpot.Counters[i].hasError()) {
                    return true;
                }
            }
        }

        return false;
    }

    private static getCustomContent(name: string, success: Function, error: Function): void {
        if (AppContext.gameConfiguration == null) {
            if (error) error();
            return;
        }

        if (AppContext.gameConfiguration.StaticContentUrl == "") {
            if (error) error();
            return;
        }

        let theme = AppContext.gameConfiguration.ProgressiveBonusTheme;
        if (theme == "") {
            if (AppContext.gameConfiguration.UseJackpotLocalization) {
                theme = Jackpot.JackpotTheme;
            } else {
                if (error) error();
                return;
            }
        }

        const staticContentUrl = AppContext.gameConfiguration.StaticContentUrl;
        const spbFolder = "SynotProgressiveBonus";
        const url = `${staticContentUrl}${spbFolder}/${theme}/${window["QUALITY"]}/${name}`;
        let img = document.createElement("img");
        img.setAttribute("src", url);

        img.addEventListener("load", (ev) => {
            if (success) success(url);
        });
        img.addEventListener("error", (ev) => {
            if (error) error();
        });
    }
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/Channels/Common/Bonuses/Jackpot.ts
 **/